/* Feb-2026 · Author Sync Report panel.
 *
 * Route:  /studio/sync-report
 * Backend:
 *   GET /api/sync-report/summary   (public)
 *   GET /api/sync-report/my-books  (signed-in author)
 *
 * Shows the Companion Reader's 4-hourly `/companion/shelves` pull
 * activity: pulls in the last 24 h / 7 d / 30 d, top genres,
 * coverage (% published rows ever served), and a per-title table
 * of the author's own books with last-served-at + read count.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, TrendingUp, BookOpen, RefreshCw, PackageOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ax  = axios.create({ baseURL: `${API}/api`, withCredentials: true });

function fmtWhen(iso) {
  if (!iso) return 'never';
  try {
    const d = new Date(iso);
    const min = Math.round((Date.now() - d.getTime()) / 60000);
    if (min < 1)  return 'just now';
    if (min < 60) return `${min}m ago`;
    const hrs = Math.round(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  } catch { return 'never'; }
}

export default function SyncReportPage() {
  const [summary, setSummary] = useState(null);
  const [books,   setBooks]   = useState([]);
  const [busy,    setBusy]    = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [s, b] = await Promise.all([
        ax.get('/sync-report/summary').then((r) => r.data),
        ax.get('/sync-report/my-books').then((r) => r.data).catch(() => ({ books: [] })),
      ]);
      setSummary(s);
      setBooks(b.books || []);
    } catch (e) {
      toast.error('Failed to load sync report.');
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  if (!summary) {
    return (
      <div className="p-8 text-[13px] text-muted-foreground"
           data-testid="sync-report-loading">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading sync report…
      </div>
    );
  }

  const w = summary.windows;
  const cov = summary.coverage;

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-6" data-testid="sync-report-page">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-serif text-2xl">Sync Report</h1>
          <p className="text-[12px] text-muted-foreground">
            The Companion Reader app pulls the shelf every 4 hours. This is
            where you see the pulls landing.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy}
                data-testid="sync-report-refresh-btn"
                className="h-8 gap-1 text-[12px]">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </header>

      <section className="grid grid-cols-3 gap-3" data-testid="sync-report-windows">
        {[
          { key: 'last_24h', label: 'Last 24 h', data: w.last_24h },
          { key: 'last_7d',  label: 'Last 7 d',  data: w.last_7d  },
          { key: 'last_30d', label: 'Last 30 d', data: w.last_30d },
        ].map(({ key, label, data }) => (
          <div key={key}
               className="rounded-md border border-border bg-card p-4"
               data-testid={`sync-report-window-${key}`}>
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
              {label}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-mono text-2xl">{data.pulls}</span>
              <span className="text-[11.5px] text-muted-foreground">pulls</span>
            </div>
            <div className="text-[11.5px] text-muted-foreground">
              {data.rows_served.toLocaleString()} rows served
            </div>
          </div>
        ))}
      </section>

      <section className="rounded-md border border-border bg-card p-4">
        <div className="mb-2 flex items-center gap-2 text-[12.5px] font-semibold">
          <TrendingUp className="h-4 w-4 text-primary" />
          Last pull cursor
        </div>
        {summary.last_pull?.at ? (
          <div className="grid grid-cols-2 gap-2 text-[12px] sm:grid-cols-4"
               data-testid="sync-report-last-pull">
            <Cell label="When"        value={fmtWhen(summary.last_pull.at)} />
            <Cell label="Rows served" value={summary.last_pull.count} />
            <Cell label="Genre"       value={summary.last_pull.genre || 'all'} />
            <Cell label="Incremental" value={summary.last_pull.since ? 'yes' : 'full'} />
          </div>
        ) : (
          <p className="text-[12px] text-muted-foreground">No pulls recorded yet.</p>
        )}
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-md border border-border bg-card p-4">
          <div className="mb-2 text-[12.5px] font-semibold">Coverage</div>
          <div className="space-y-1 text-[12px]"
               data-testid="sync-report-coverage">
            <div>Published titles: <b>{cov.published_total}</b></div>
            <div>Ever pulled by reader: <b>{cov.ever_served}</b></div>
            <div className={cov.never_served > 0 ? 'text-amber-700' : 'text-emerald-700'}>
              Never served: <b>{cov.never_served}</b>
            </div>
          </div>
        </div>

        <div className="rounded-md border border-border bg-card p-4">
          <div className="mb-2 text-[12.5px] font-semibold">Top genres · last 7 d</div>
          {summary.top_genres_7d?.length ? (
            <ul className="space-y-1 text-[12px]" data-testid="sync-report-top-genres">
              {summary.top_genres_7d.map((g) => (
                <li key={g.genre} className="flex justify-between">
                  <span className="capitalize">{g.genre.replace(/-/g, ' ')}</span>
                  <span className="font-mono text-muted-foreground">{g.pulls}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[12px] text-muted-foreground">No pulls yet this week.</p>
          )}
        </div>
      </section>

      <section className="rounded-md border border-border bg-card p-4"
               data-testid="sync-report-my-books">
        <div className="mb-2 flex items-center gap-2 text-[12.5px] font-semibold">
          <BookOpen className="h-4 w-4 text-primary" />
          Your titles &amp; last read
        </div>
        {books.length === 0 ? (
          <div className="flex items-center gap-2 rounded border border-dashed border-border bg-background/60 px-3 py-4 text-[12px] text-muted-foreground">
            <PackageOpen className="h-4 w-4" />
            No published titles under your account yet — publish one from Loom Studio.
          </div>
        ) : (
          <table className="w-full text-[12px]">
            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="pb-1 text-left">Title</th>
                <th className="pb-1 text-left">Genre</th>
                <th className="pb-1 text-right">Reads</th>
                <th className="pb-1 text-right">Last read</th>
              </tr>
            </thead>
            <tbody>
              {books.map((b) => (
                <tr key={b.id} className="border-t border-border"
                    data-testid={`sync-report-book-${b.id}`}>
                  <td className="py-1 pr-2">{b.title}</td>
                  <td className="py-1 pr-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                    {b.genre_slug}
                  </td>
                  <td className="py-1 pr-2 text-right font-mono">
                    {b.shelf_read_count ?? 0}
                  </td>
                  <td className={`py-1 pr-2 text-right ${b.last_shelf_read_at ? '' : 'text-amber-700'}`}>
                    {fmtWhen(b.last_shelf_read_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div className="rounded border border-border bg-background/60 px-2 py-1.5">
      <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-[13px]">{value}</div>
    </div>
  );
}
